import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#F8F9FB]">
      <SEO
        title="Privacy Policy"
        description="How NeuroNest collects, uses, stores, and protects your information. We never sell your data. Your child's data is protected with extra care."
        path="/privacy"
      />
      <Navbar />
      <main className="pt-28 pb-20 px-6 md:px-12 lg:px-20">
        <article className="max-w-4xl mx-auto">
          <header className="mb-12">
            <h1
              data-testid="privacy-title"
              className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-4"
            >
              Privacy Policy
            </h1>
            <p className="text-sm text-[#8B95A5]">
              Last Updated: February 2026 &middot; Effective Date: February 2026
            </p>
          </header>

          {/* Plain Language Summary */}
          <Section title="Plain Language Summary">
            <p className="mb-4">Before the legal language, here's what matters:</p>
            <SummaryList
              items={[
                "We collect only what's needed to make the app work for you",
                "We never sell your data to anyone, ever",
                "We don't track you for advertising purposes",
                "Voice recordings are processed in-memory and immediately discarded — never stored",
                "Your child's data is protected with extra care",
                "You can delete your account and all associated data at any time",
              ]}
            />
          </Section>

          {/* 1. Introduction */}
          <Section title="1. Introduction">
            <p className="mb-3">
              NeuroNest ("we," "our," or "us") is committed to protecting your
              privacy. This Privacy Policy explains how we collect, use, store,
              and protect your information when you use the NeuroNest mobile
              application ("the App").
            </p>
            <p>
              NeuroNest is designed to support neurodivergent individuals and
              their families. We take special care with the data we handle
              because we understand the sensitivity of the information our users
              entrust to us.
            </p>
          </Section>

          {/* 2. Information We Collect */}
          <Section title="2. Information We Collect">
            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              2.1 Information You Provide
            </h3>
            <DataTable
              headers={["Data Type", "What We Collect", "Why"]}
              rows={[
                ["Account Information", "Email address, password", "To create and secure your account"],
                ["Profile Information", "Display name, preferred name, pronouns (optional)", "To personalise your experience"],
                ["Sensory Profile", "Sensory sensitivity ratings (light, sound, touch, smell, taste, movement)", "To match you with suitable places and customise the experience"],
                ["Check-In Data", "Mood ratings, energy levels, sound levels, environment tags, optional notes", "To help you track emotional patterns"],
                ["Routine Data", "Routine names, steps, schedules, reminder times", "To provide your visual routine support"],
                ["Communication Cards", "Card text, categories, icons", "To support your communication needs"],
                ["Safe Places", "Place names, optional coordinates, personal notes", "To help you manage your safe environments"],
                ["Community Place Interactions", "Places you save or rate from the community directory", "To personalise place recommendations"],
                ["Voice Input (Sensory Coach)", "Short microphone recordings while you hold the record button", "Transcribed in-memory by OpenAI Whisper, then immediately discarded. Never stored."],
                ["Sensory Coach Conversations", "Text messages you send to the AI Coach", "Processed by Anthropic Claude to generate personalised sensory advice"],
                ["Family Circle Data", "Shared child profiles, invite codes", "To enable co-parenting and shared care coordination"],
              ]}
            />

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-8 mb-3">
              2.2 Information Collected Automatically
            </h3>
            <DataTable
              headers={["Data Type", "What We Collect", "Why"]}
              rows={[
                ["Device Information", "Device type, operating system version", "To ensure the app works correctly on your device"],
                ["Push Notification Tokens", "Firebase Cloud Messaging token", "To deliver the notifications you've opted into"],
                ["Tool Usage Data", "Which calm toolkit features you use and when (aggregated)", "To notify parents/carers of child activity (in parent-child mode only)"],
              ]}
            />

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-8 mb-3">
              2.3 Information We Do NOT Collect
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed">
              <li className="flex items-start gap-2"><Dot />We do <strong>not</strong> collect your precise location continuously — location is only accessed when you explicitly use the "Use My Location" feature</li>
              <li className="flex items-start gap-2"><Dot />We do <strong>not</strong> collect browsing history or activity outside the app</li>
              <li className="flex items-start gap-2"><Dot />We do <strong>not</strong> use cookies for advertising or tracking</li>
              <li className="flex items-start gap-2"><Dot />We do <strong>not</strong> collect biometric data</li>
              <li className="flex items-start gap-2"><Dot />We do <strong>not</strong> retain audio recordings — when you use the Sensory Coach voice feature, your microphone audio is captured only while you hold the record button, transmitted securely to our backend, which immediately forwards it to OpenAI Whisper for transcription. The audio is held only in memory during this short request and is never written to disk on our servers or retained by OpenAI under their zero-retention API terms.</li>
              <li className="flex items-start gap-2"><Dot />We do <strong>not</strong> access your microphone in the background — microphone access is requested only when you tap the voice record button in the Sensory Coach</li>
            </ul>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-8 mb-3">
              2.4 Microphone & Notification Permissions
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed">
              <li className="flex items-start gap-2"><Dot /><strong>Microphone:</strong> Requested only when you tap the voice record button in the Sensory Coach. Audio is captured for the duration of your press, transmitted securely for transcription, and never stored.</li>
              <li className="flex items-start gap-2"><Dot /><strong>Push Notifications:</strong> Requested to deliver routine reminders, check-in nudges, and (in Family Mode) parent alerts when a child checks in or completes a routine. You can disable notifications at any time in your device settings.</li>
            </ul>
          </Section>

          {/* 3. How We Use Your Information */}
          <Section title="3. How We Use Your Information">
            <p className="mb-4">We use the information we collect to:</p>
            <ol className="list-decimal list-inside space-y-2 text-[#5A6376] text-sm leading-relaxed mb-6">
              <li><strong>Provide the App's features</strong> — check-ins, routines, communication cards, community places, calm toolkit, AI Sensory Coach</li>
              <li><strong>Personalise your experience</strong> — match community places to your sensory profile, display your preferred name, provide context-aware AI coaching</li>
              <li><strong>Send notifications you've requested</strong> — routine reminders, check-in reminders, parent-child activity alerts</li>
              <li><strong>Improve the App</strong> — understand which features are used to prioritise development (using aggregated, anonymised data only)</li>
              <li><strong>Generate insights</strong> — provide AI-powered weekly summaries of your emotional patterns (processed securely, never shared)</li>
            </ol>
            <p className="mb-3">We will <strong>never</strong> use your information to:</p>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed">
              <li className="flex items-start gap-2"><Dot />Display advertisements</li>
              <li className="flex items-start gap-2"><Dot />Build advertising profiles</li>
              <li className="flex items-start gap-2"><Dot />Sell or rent to third parties</li>
              <li className="flex items-start gap-2"><Dot />Make automated decisions that affect your access to services</li>
            </ul>
          </Section>

          {/* 4. Children's Privacy */}
          <Section title="4. Children's Privacy">
            <p className="mb-4">
              NeuroNest includes a <strong>Parent/Carer Mode</strong> and a{" "}
              <strong>Child Mode</strong>. We take children's privacy seriously
              and comply with applicable children's data protection regulations.
            </p>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              How Child Accounts Work
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed mb-6">
              <li className="flex items-start gap-2"><Dot />Child accounts are created by a parent or carer</li>
              <li className="flex items-start gap-2"><Dot />Children log in using a simple 4-digit PIN (not an email/password)</li>
              <li className="flex items-start gap-2"><Dot />The child's account is linked to the parent's account</li>
              <li className="flex items-start gap-2"><Dot />Parents can view their child's check-ins, routine completions, and tool usage</li>
            </ul>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              Data We Collect From Child Users
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed mb-6">
              <li className="flex items-start gap-2"><Dot />Display name (provided by the parent)</li>
              <li className="flex items-start gap-2"><Dot />Check-in data (mood, energy, sound levels)</li>
              <li className="flex items-start gap-2"><Dot />Routine completion status</li>
              <li className="flex items-start gap-2"><Dot />Tool usage (which calm toolkit features are used)</li>
            </ul>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              Parental Controls
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed mb-6">
              <li className="flex items-start gap-2"><Dot />Parents can view all child activity data</li>
              <li className="flex items-start gap-2"><Dot />Parents can manage routines and settings for their child</li>
              <li className="flex items-start gap-2"><Dot />The child's settings screen is locked behind the parent's password</li>
              <li className="flex items-start gap-2"><Dot />Parents can delete the child's account and all associated data</li>
            </ul>

            <p className="text-[#5A6376] text-sm leading-relaxed">
              <strong>Consent:</strong> By creating a child account, the parent
              or carer confirms they have the legal authority to consent on
              behalf of the child and agrees to this Privacy Policy on the
              child's behalf.
            </p>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              AI Processing for Children
            </h3>
            <p className="text-[#5A6376] text-sm leading-relaxed mb-4">
              When a child uses the Sensory Coach, their typed messages and (if they use the mic) transient voice input are processed by third-party AI providers (Anthropic Claude, OpenAI Whisper) under zero-retention terms. We do not allow these providers to use children's data for model training. We do not share children's personal identifiers (email, real name, date of birth) with any AI provider — only the message content needed to generate a reply.
            </p>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              COPPA & Families Policy Compliance
            </h3>
            <p className="text-[#5A6376] text-sm leading-relaxed">
              NeuroNest complies with the U.S. Children's Online Privacy Protection Act (COPPA), the UK Age Appropriate Design Code, and Google Play's Families Policy. We do not knowingly collect personal information from children under 13 (or the equivalent age in your jurisdiction) without verifiable parental consent obtained through the parent's account creation.
            </p>
          </Section>

          {/* 5. Third-Party Services */}
          <Section title="5. Third-Party Services">
            <p className="mb-4">
              We use the following third-party services to operate the App:
            </p>
            <DataTable
              headers={["Service", "Purpose", "Data Shared"]}
              rows={[
                ["Supabase", "Database and authentication", "Account data, app data"],
                ["Firebase Cloud Messaging", "Push notifications", "Device token, notification content"],
                ["Anthropic (Claude Haiku)", "AI Sensory Coach chat replies", "Coach message text only (no PII, no email)"],
                ["OpenAI Whisper", "Voice transcription for Sensory Coach", "Transient audio (not retained; subject to OpenAI's zero-retention API terms)"],
                ["OpenAI TTS (tts-1-hd)", "Reading Coach replies aloud, weekly insight summaries", "Coach reply text, anonymised check-in data"],
                ["Google Maps/Places API", "Community place information", "Place IDs (not user data)"],
                ["Resend", "Transactional emails (welcome, password reset, family invites, weekly insights)", "Email address, display name"],
              ]}
            />
            <p className="mt-4 text-[#5A6376] text-sm leading-relaxed">
              We do <strong>not</strong> share your personal information with
              these services beyond what is strictly necessary for their
              function.
            </p>
          </Section>

          {/* 6. Data Storage and Security */}
          <Section title="6. Data Storage and Security">
            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-4 mb-3">
              How We Protect Your Data
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed mb-6">
              <li className="flex items-start gap-2"><Dot />All data is transmitted over encrypted connections (HTTPS/TLS)</li>
              <li className="flex items-start gap-2"><Dot />Passwords are hashed and never stored in plain text</li>
              <li className="flex items-start gap-2"><Dot />Child PINs are hashed with salt before storage</li>
              <li className="flex items-start gap-2"><Dot />Database access is restricted with Row Level Security (RLS) policies</li>
              <li className="flex items-start gap-2"><Dot />We conduct regular security reviews of our codebase</li>
            </ul>

            <h3 className="font-heading text-lg font-semibold text-[#2F3A56] mt-6 mb-3">
              Data Retention
            </h3>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed">
              <li className="flex items-start gap-2"><Dot />Your data is retained for as long as your account is active</li>
              <li className="flex items-start gap-2"><Dot />If you delete your account, all data is permanently deleted within 30 days</li>
              <li className="flex items-start gap-2"><Dot />Anonymised, aggregated data may be retained for service improvement</li>
            </ul>
          </Section>

          {/* 7. Your Rights */}
          <Section title="7. Your Rights">
            <p className="mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <DataTable
              headers={["Right", "Description"]}
              rows={[
                ["Access", "Request a copy of all personal data we hold about you"],
                ["Correction", "Request correction of inaccurate personal data"],
                ["Deletion", "Request deletion of your account and all associated data"],
                ["Portability", "Request your data in a machine-readable format"],
                ["Restriction", "Request that we limit how we use your data"],
                ["Objection", "Object to certain types of data processing"],
                ["Withdraw Consent", "Withdraw consent for data processing at any time"],
              ]}
            />
            <p className="mt-4 text-[#5A6376] text-sm leading-relaxed">
              To exercise any of these rights, please contact us at:{" "}
              <a
                href="mailto:hello@neuronest.global"
                className="text-brand-teal hover:underline"
              >
                hello@neuronest.global
              </a>
              . You can also request account deletion via our self-serve form at{" "}
              <a
                href="https://www.neuronest.global/delete-account"
                className="text-brand-teal hover:underline"
              >
                neuronest.global/delete-account
              </a>
              . We will respond to all requests within 30 days.
            </p>
          </Section>

          {/* 8. UK GDPR */}
          <Section title="8. UK GDPR and Data Protection Act 2018">
            <p className="mb-3">
              If you are located in the United Kingdom, the following applies:
            </p>
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed">
              <li className="flex items-start gap-2"><Dot />We process your data under the lawful bases of <strong>consent</strong> (for optional features) and <strong>contract performance</strong> (for core functionality)</li>
              <li className="flex items-start gap-2"><Dot />You have the right to lodge a complaint with the <strong>Information Commissioner's Office (ICO)</strong> at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-brand-teal hover:underline">ico.org.uk</a></li>
            </ul>
          </Section>

          {/* 9. Changes */}
          <Section title="9. Changes to This Privacy Policy">
            <ul className="space-y-2 text-[#5A6376] text-sm leading-relaxed">
              <li className="flex items-start gap-2"><Dot />We will update the "Last Updated" date at the top</li>
              <li className="flex items-start gap-2"><Dot />For significant changes, we will notify you within the App</li>
              <li className="flex items-start gap-2"><Dot />Continued use of the App after changes constitutes acceptance</li>
            </ul>
          </Section>

          {/* 10. Contact Us */}
          <Section title="10. Contact Us">
            <p className="text-[#5A6376] text-sm leading-relaxed">
              If you have any questions about this Privacy Policy, your data, or
              your rights, please contact us:
            </p>
            <p className="mt-3 text-[#5A6376] text-sm">
              Email:{" "}
              <a
                href="mailto:hello@neuronest.global"
                data-testid="privacy-contact-email"
                className="text-brand-teal hover:underline"
              >
                hello@neuronest.global
              </a>
            </p>
          </Section>

          {/* 11. Accessibility */}
          <Section title="11. Accessibility of This Policy">
            <p className="text-[#5A6376] text-sm leading-relaxed">
              This Privacy Policy is designed to be readable and understandable.
              If you need this policy in a different format (larger text, audio,
              simplified language), please contact us and we will do our best to
              accommodate your needs.
            </p>
          </Section>

          <div className="mt-12 pt-8 border-t border-[#E7E9EC] text-center">
            <p className="text-sm text-[#8B95A5] italic">
              NeuroNest — Your sensory-support and predictability companion
            </p>
            <p className="text-xs text-[#8B95A5] mt-1">
              "Your data, like your experience, is yours."
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-[#2F3A56] mb-4">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Dot() {
  return (
    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" />
  );
}

function SummaryList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-start gap-3 text-[#5A6376] text-sm leading-relaxed"
        >
          <span className="mt-1.5 w-2 h-2 rounded-full bg-brand-teal flex-shrink-0" />
          <strong>{item}</strong>
        </li>
      ))}
    </ul>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#E7E9EC]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F0F1F3]">
            {headers.map((h) => (
              <th
                key={h}
                className="text-left px-4 py-3 font-semibold text-[#2F3A56] text-xs uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={i % 2 === 0 ? "bg-white" : "bg-[#F8F9FB]"}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-4 py-3 text-[#5A6376] leading-relaxed border-t border-[#E7E9EC]"
                >
                  {j === 0 ? (
                    <strong className="text-[#2F3A56]">{cell}</strong>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
